import { useCallback, useEffect, useState } from 'react'
import { Drawer, Pagination, Table, Tabs } from '@arco-design/web-react'
import { getChannelBalanceLedgerApi, getChannelCostLogsApi } from '@/api/channels'
import type { Channel, ChannelBalanceAdjustment, ChannelCostLog } from '@/types'
import { formatMicroUsd, renderQuota } from '@/utils/quota'
import { renderModelTag, renderTimestamp } from '@/utils/render'
import { drawerWidth } from '@/utils/responsive'

interface Props {
  visible: boolean
  channel: Channel | null
  onClose: () => void
}

const PAGE_SIZE = 10

export default function ChannelBalanceLedgerDrawer({ visible, channel, onClose }: Props) {
  const [activeTab, setActiveTab] = useState('adjustments')
  const [adjPage, setAdjPage] = useState(1)
  const [costPage, setCostPage] = useState(1)
  const [adjLoading, setAdjLoading] = useState(false)
  const [costLoading, setCostLoading] = useState(false)
  const [adjustments, setAdjustments] = useState<ChannelBalanceAdjustment[]>([])
  const [adjustmentsTotal, setAdjustmentsTotal] = useState(0)
  const [costLogs, setCostLogs] = useState<ChannelCostLog[]>([])
  const [costLogsTotal, setCostLogsTotal] = useState(0)

  const loadAdjustments = useCallback(async () => {
    if (!channel) return
    setAdjLoading(true)
    try {
      const res = await getChannelBalanceLedgerApi(channel.id, { page: adjPage, page_size: PAGE_SIZE })
      setAdjustments(res.items)
      setAdjustmentsTotal(res.total)
    } finally {
      setAdjLoading(false)
    }
  }, [adjPage, channel])

  const loadCostLogs = useCallback(async () => {
    if (!channel) return
    setCostLoading(true)
    try {
      const res = await getChannelCostLogsApi(channel.id, { page: costPage, page_size: PAGE_SIZE })
      setCostLogs(res.items)
      setCostLogsTotal(res.total)
    } finally {
      setCostLoading(false)
    }
  }, [channel, costPage])

  useEffect(() => {
    if (!visible || !channel) return
    setAdjPage(1)
    setCostPage(1)
    setActiveTab('adjustments')
  }, [channel, visible])

  useEffect(() => {
    if (!visible || !channel || activeTab !== 'adjustments') return
    void loadAdjustments()
  }, [activeTab, channel, loadAdjustments, visible])

  useEffect(() => {
    if (!visible || !channel || activeTab !== 'costs') return
    void loadCostLogs()
  }, [activeTab, channel, loadCostLogs, visible])

  const costSourceLabel = (source: string) => {
    if (source === 'model_table') return '模型成本表'
    if (source === 'default_ratio') return '默认比例'
    return source || '-'
  }

  return (
    <Drawer
      title={channel ? `余额台账 · ${channel.name}` : '余额台账'}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={drawerWidth(760)}
      unmountOnExit
    >
      <Tabs activeTab={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane key="adjustments" title="调整审计">
          <Table
            rowKey="id"
            loading={adjLoading}
            data={adjustments}
            pagination={false}
            scroll={{ x: 680 }}
            columns={[
              { title: '时间', dataIndex: 'createdAt', width: 160, render: renderTimestamp },
              {
                title: '变动',
                dataIndex: 'deltaMicroUsd',
                width: 120,
                render: (v: number) => (
                  <span className={`tabular-nums ${v >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                    {v >= 0 ? '+' : ''}
                    {formatMicroUsd(v)}
                  </span>
                ),
              },
              {
                title: '调整前 → 后',
                width: 180,
                render: (_: unknown, row: ChannelBalanceAdjustment) => (
                  <span className="text-xs tabular-nums">
                    {formatMicroUsd(row.beforeMicroUsd)} → {formatMicroUsd(row.afterMicroUsd)}
                  </span>
                ),
              },
              { title: '原因', dataIndex: 'reason', ellipsis: true },
              { title: '操作人 ID', dataIndex: 'adminUserId', width: 90 },
            ]}
            noDataElement={<div className="py-8 text-center text-sm text-slate-500">暂无调整记录</div>}
          />
          {adjustmentsTotal > PAGE_SIZE ? (
            <div className="mt-3 flex justify-end">
              <Pagination
                current={adjPage}
                pageSize={PAGE_SIZE}
                total={adjustmentsTotal}
                onChange={setAdjPage}
                size="small"
              />
            </div>
          ) : null}
        </Tabs.TabPane>
        <Tabs.TabPane key="costs" title="成本流水">
          <Table
            rowKey="id"
            loading={costLoading}
            data={costLogs}
            pagination={false}
            scroll={{ x: 900 }}
            columns={[
              { title: '时间', dataIndex: 'createdAt', width: 160, render: renderTimestamp },
              {
                title: '模型',
                dataIndex: 'modelName',
                width: 140,
                render: (v: string) => (v ? renderModelTag(v) : '-'),
              },
              {
                title: 'Token',
                width: 120,
                render: (_: unknown, row: ChannelCostLog) => (
                  <span className="text-xs tabular-nums">
                    {row.promptTokens}/{row.completionTokens}
                  </span>
                ),
              },
              {
                title: '用户扣费',
                dataIndex: 'userQuota',
                width: 110,
                render: (v: number) => renderQuota(v),
              },
              {
                title: '渠道成本',
                dataIndex: 'channelCostMicroUsd',
                width: 110,
                render: (v: number) => formatMicroUsd(v),
              },
              {
                title: '来源',
                dataIndex: 'costSource',
                width: 100,
                render: costSourceLabel,
              },
              {
                title: '扣减后余额',
                dataIndex: 'balanceAfterMicroUsd',
                width: 120,
                render: (v: number) => formatMicroUsd(v),
              },
              {
                title: '请求 ID',
                dataIndex: 'requestId',
                width: 140,
                ellipsis: true,
                render: (v: string) => <code className="text-[11px]">{v || '-'}</code>,
              },
            ]}
            noDataElement={<div className="py-8 text-center text-sm text-slate-500">暂无成本流水</div>}
          />
          {costLogsTotal > PAGE_SIZE ? (
            <div className="mt-3 flex justify-end">
              <Pagination
                current={costPage}
                pageSize={PAGE_SIZE}
                total={costLogsTotal}
                onChange={setCostPage}
                size="small"
              />
            </div>
          ) : null}
        </Tabs.TabPane>
      </Tabs>
    </Drawer>
  )
}
